import React, { useState } from 'react'
import { SingleChoiceWidget } from './SingleChoiceWidget'
import { MultipleChoiceWidget } from './MultipleChoiceWidget'
import { RatingWidget } from './RatingWidget'
import { NpsWidget } from './NpsWidget'
import { RankingWidget } from './RankingWidget'

// Local widget config types (mirrors @knownow/domain — no import)

export type SingleChoiceConfig = {
  type: 'single_choice'
  options: string[]
}

export type MultipleChoiceConfig = {
  type: 'multiple_choice'
  options: string[]
  minSelections?: number
  maxSelections?: number
}

export type RatingConfig = {
  type: 'rating'
  min: number
  max: number
  labels?: { low?: string; high?: string }
}

export type NpsConfig = {
  type: 'nps'
}

export type RankingConfig = {
  type: 'ranking'
  items: string[]
}

export type WidgetConfig =
  | SingleChoiceConfig
  | MultipleChoiceConfig
  | RatingConfig
  | NpsConfig
  | RankingConfig

// Local widget response types (mirrors @knownow/domain — no import)

export type SingleChoiceResponse = {
  type: 'single_choice'
  selectedOption: string
}

export type MultipleChoiceResponse = {
  type: 'multiple_choice'
  selectedOptions: string[]
}

export type RatingResponse = {
  type: 'rating'
  value: number
}

export type NpsResponse = {
  type: 'nps'
  score: number
}

export type RankingResponse = {
  type: 'ranking'
  orderedItems: string[]
}

export type WidgetResponse =
  | SingleChoiceResponse
  | MultipleChoiceResponse
  | RatingResponse
  | NpsResponse
  | RankingResponse

export interface ChatWidgetProps {
  config: WidgetConfig
  onSubmit: (response: WidgetResponse) => void
  disabled?: boolean
}

type InternalState =
  | { type: 'single_choice'; value: string | null }
  | { type: 'multiple_choice'; value: string[] }
  | { type: 'rating'; value: number | null }
  | { type: 'nps'; value: number | null }
  | { type: 'ranking'; value: string[] }

function createInitialState(config: WidgetConfig): InternalState {
  switch (config.type) {
    case 'single_choice':
      return { type: 'single_choice', value: null }
    case 'multiple_choice':
      return { type: 'multiple_choice', value: [] }
    case 'rating':
      return { type: 'rating', value: null }
    case 'nps':
      return { type: 'nps', value: null }
    case 'ranking':
      return { type: 'ranking', value: [...config.items] }
  }
}

function isResponseValid(
  state: InternalState,
  config: WidgetConfig,
): boolean {
  switch (state.type) {
    case 'single_choice':
      return state.value !== null
    case 'multiple_choice': {
      const mc = config as MultipleChoiceConfig
      const count = state.value.length
      if (count === 0) return false
      if (mc.minSelections !== undefined && count < mc.minSelections) {
        return false
      }
      return true
    }
    case 'rating':
      return state.value !== null
    case 'nps':
      return state.value !== null
    case 'ranking':
      return state.value.length > 0
  }
}

function buildResponse(state: InternalState): WidgetResponse | null {
  switch (state.type) {
    case 'single_choice':
      return state.value !== null
        ? { type: 'single_choice', selectedOption: state.value }
        : null
    case 'multiple_choice':
      return state.value.length > 0
        ? { type: 'multiple_choice', selectedOptions: state.value }
        : null
    case 'rating':
      return state.value !== null
        ? { type: 'rating', value: state.value }
        : null
    case 'nps':
      return state.value !== null
        ? { type: 'nps', score: state.value }
        : null
    case 'ranking':
      return state.value.length > 0
        ? { type: 'ranking', orderedItems: state.value }
        : null
  }
}

export function ChatWidget({
  config,
  onSubmit,
  disabled = false,
}: ChatWidgetProps): React.ReactElement {
  const [state, setState] = useState<InternalState>(() =>
    createInitialState(config),
  )
  const [submitted, setSubmitted] = useState(false)

  const isValid = isResponseValid(state, config)
  const isDisabled = disabled || submitted

  function handleSubmit(): void {
    if (!isValid || isDisabled) return
    const response = buildResponse(state)
    if (response !== null) {
      setSubmitted(true)
      onSubmit(response)
    }
  }

  function renderWidget(): React.ReactElement {
    switch (config.type) {
      case 'single_choice':
        return (
          <SingleChoiceWidget
            options={config.options}
            value={
              state.type === 'single_choice' ? state.value : null
            }
            onChange={(v) =>
              setState({ type: 'single_choice', value: v })
            }
            disabled={isDisabled}
          />
        )
      case 'multiple_choice':
        return (
          <MultipleChoiceWidget
            options={config.options}
            value={
              state.type === 'multiple_choice' ? state.value : []
            }
            onChange={(v) =>
              setState({ type: 'multiple_choice', value: v })
            }
            disabled={isDisabled}
            {...(config.minSelections !== undefined
              ? { minSelections: config.minSelections }
              : {})}
            {...(config.maxSelections !== undefined
              ? { maxSelections: config.maxSelections }
              : {})}
          />
        )
      case 'rating':
        return (
          <RatingWidget
            min={config.min}
            max={config.max}
            value={state.type === 'rating' ? state.value : null}
            onChange={(v) =>
              setState({ type: 'rating', value: v })
            }
            disabled={isDisabled}
            {...(config.labels !== undefined
              ? { labels: config.labels }
              : {})}
          />
        )
      case 'nps':
        return (
          <NpsWidget
            value={state.type === 'nps' ? state.value : null}
            onChange={(v) =>
              setState({ type: 'nps', value: v })
            }
            disabled={isDisabled}
          />
        )
      case 'ranking':
        return (
          <RankingWidget
            items={config.items}
            value={state.type === 'ranking' ? state.value : []}
            onChange={(v) =>
              setState({ type: 'ranking', value: v })
            }
            disabled={isDisabled}
          />
        )
    }
  }

  return (
    <div
      data-testid="chat-widget"
      className="flex flex-col gap-4 font-sans"
    >
      {renderWidget()}

      <button
        type="button"
        disabled={!isValid || isDisabled}
        onClick={handleSubmit}
        className={[
          'self-end px-6 py-2 rounded-lg text-sm font-medium transition-colors',
          'focus:outline-hidden focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
          isValid && !isDisabled
            ? 'bg-brand-500 text-neutral-0 hover:bg-brand-600 active:bg-brand-700 cursor-pointer'
            : 'bg-neutral-200 text-neutral-400 cursor-not-allowed',
        ].join(' ')}
        data-testid="chat-widget-submit"
      >
        {submitted ? 'Submitted' : 'Submit'}
      </button>
    </div>
  )
}
